import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { getPageNumber, initialize, onRowSelect } from '@grid';

import { LazyLoadEvent, MenuItem, SortMeta } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { EnvironmentService, GridActions, GridService } from '@core/services';

import { ColumnFilter, Filters, SelectRowEvent, SelectedRow, Sorts } from '#types/grid';
import { ItemLookupResponseRow } from '#types/item';

import { CONSTANTS } from '@item/constants';
import { ItemService } from '@item/services';
import { ItemActions } from '@item/state';

import { columnDefinition } from './grid.columns';

@Component({
  selector: 'z-item-lookup-dialog-grid',
  templateUrl: './grid.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemLookupDialogGridComponent implements OnInit, OnDestroy {
  destroy$ = new Subject<void>();

  selectedRow: SelectedRow<ItemLookupResponseRow>;

  sorts$ = new BehaviorSubject<SortMeta[]>(null);
  filters$ = new BehaviorSubject<ColumnFilter>(null);
  contextMenuItems$ = new BehaviorSubject<MenuItem[]>([]);

  defaultFilters: Filters = {};

  gridId = CONSTANTS.GRID_ITEM_LOOKUP_DIALOG;

  initialized = false;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private store: Store,
    public itemService: ItemService,
    public environmentService: EnvironmentService,
    public gridService: GridService,
    private translocoService: TranslocoService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig
  ) {}

  ngOnInit() {
    this.itemService.itemLookupDialogDimensions$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.gridService.height$.next({ id: this.gridId, payload: `${value.grid.height}px` });
    });

    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe((translations) => {
        this.generateContextMenuItems();

        this.store.dispatch(
          GridActions.Initialize({
            id: this.gridId,
            grid: initialize<ItemLookupResponseRow>(columnDefinition, translations, {}, { filters: this.defaultFilters }),
          })
        );
      });

    this.gridService
      .initialized$(this.gridId)
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe(() => {
        this.store.dispatch(GridActions.OverrideFilters({ id: this.gridId, filters: this.defaultFilters }));

        this.gridService
          .data$(this.gridId)
          .pipe(take(1))
          .subscribe((data) => {
            this.gridService
              .sorts$(this.gridId)
              .pipe(
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                takeUntil(this.destroy$)
              )
              .subscribe((sorts) => {
                this.sorts$.next(sorts);
              });

            this.gridService
              .filters$(this.gridId)
              .pipe(
                filter((x) => !!x),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                takeUntil(this.destroy$)
              )
              .subscribe((filters) => {
                const filterMetadata: ColumnFilter = {};

                if (filters) {
                  for (const [field, entry] of Object.entries(filters)) {
                    filterMetadata[field] = {
                      matchMode: entry.matchMode,
                      operator: entry.operator,
                      value: entry.value,
                    };
                  }
                }

                this.filters$.next(filterMetadata);

                if (this.initialized) {
                  this.requestData();
                }
              });

            if (!data || data.length === 0) {
              this.requestData();
            }

            setTimeout(() => {
              this.initialized = true;
            }, 0);
          });
      });

    this.translocoService.events$
      .pipe(
        filter((event) => event.type === 'langChanged'),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onLanguageChange();
      });

    this.gridService.gridRefresh$
      .pipe(
        filter((id) => this.gridId === id),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.requestData();
      });

    this.gridService.gridLoad$
      .pipe(
        filter((id) => this.gridId === id),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.gridService
          .sorts$(this.gridId)
          .pipe(
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            take(1)
          )
          .subscribe((sorts) => {
            this.sorts$.next(sorts);
          });
      });

    this.gridService
      .selectedRow$<ItemLookupResponseRow>(this.gridId)
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.generateContextMenuItems(selectedRow);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.store.dispatch(GridActions.Destroy({ id: this.gridId }));
  }

  onLanguageChange() {
    this.translocoService
      .selectTranslation()
      .pipe(take(1))
      .subscribe(() => {
        this.generateContextMenuItems();
        this.gridService.updateColumnHeaders(this.gridId);
      });
  }

  requestData(filters?: Filters, sorts?: Sorts, first?: number, rows?: number) {
    if (filters !== undefined && sorts !== undefined && first !== undefined && rows !== undefined) {
      this.store.dispatch(
        ItemActions.ItemLookupDialogRequestDataWithParameters({
          id: this.gridId,
          filters,
          page: getPageNumber(first, rows),
          first,
          rows,
          sorts,
        })
      );
    } else if (filters !== undefined && sorts === undefined && first === undefined && rows === undefined) {
      this.store.dispatch(
        ItemActions.ItemLookupDialogRequestDataWithFilters({
          id: this.gridId,
          filters,
        })
      );
    } else {
      this.store.dispatch(ItemActions.ItemLookupDialogRequestData({ id: this.gridId }));
    }
  }

  generateContextMenuItems(selectedRow?: SelectedRow<ItemLookupResponseRow>) {
    const items: MenuItem[] = [];

    items.push(...this.gridService.getCopyRowMenuItem({ gridId: this.gridId, disabled: !selectedRow?.data ? true : false }));
    items.push(...this.gridService.getSeparator());

    items.push(...this.gridService.getRefreshMenuItem(this.gridId));
    items.push(...this.gridService.getResetMenuItem(this.gridId));
    items.push(...this.gridService.getDeveloperMenu(this.gridId));

    this.contextMenuItems$.next(items);
  }

  onLazyLoad = (event: LazyLoadEvent) => {
    if (!this.initialized) {
      return;
    }

    this.gridService
      .initialized$(this.gridId)
      .pipe(
        filter((x) => !!x),
        take(1)
      )
      .subscribe(() => {
        const filters: Filters = {};
        const sorts: Sorts = {};

        if (event.filters) {
          for (const [field, entry] of Object.entries(event.filters)) {
            filters[field] = {
              matchMode: entry.matchMode,
              operator: entry.operator,
              value: entry.value,
            };
          }
        }

        if (event.multiSortMeta) {
          for (const sortMeta of event.multiSortMeta) {
            sorts[sortMeta.field] = {
              order: sortMeta.order,
            };
          }
        }

        this.requestData(filters, sorts, event.first, event.rows);
      });
  };

  onSelectRow(event: SelectRowEvent<ItemLookupResponseRow>) {
    this.store.dispatch(GridActions.SelectMultipleRows({ id: this.gridId, rows: event.selectedRows }));
  }

  onRowSelect = (event: ItemLookupResponseRow) => {
    const selectedRow = onRowSelect(event);

    this.store.dispatch(GridActions.SelectRow({ id: this.gridId, selectedRow }));
  };
}
